import { useEffect, useState } from 'react';
import { api } from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Quote, Heart, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [story, setStory] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    try {
      const { data } = await api.get('/testimonials');
      setItems(data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !story.trim()) {
      toast.error('Please share your name and story');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/testimonials', {
        name: name.trim(),
        partner_name: partnerName.trim() || null,
        story: story.trim()
      });
      toast.success("Thank you! Your story has been received and will appear here once reviewed.");
      setName(''); setPartnerName(''); setStory('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit right now');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85)', backgroundSize: 'cover', backgroundPosition: 'center', filter: 'saturate(0.9) brightness(1.05)' }} aria-hidden />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg, rgba(253, 251, 247, 0.94) 0%, rgba(243, 239, 231, 0.9) 50%, rgba(253, 251, 247, 0.85) 100%)' }} aria-hidden />
        <div className="max-w-5xl mx-auto px-6 py-20 text-center relative">
          <div className="inline-flex items-center gap-3 mb-4">
            <Quote className="w-8 h-8" style={{ color: '#E57A44', strokeWidth: 1.5 }} />
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }} data-testid="testimonials-title">
              Restoration Stories
            </h1>
          </div>
          <p className="text-lg" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>
            Real journeys, real healing. Read how faith and grace restored these relationships — and share yours.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-16">
        {loading ? (
          <p className="text-center py-12" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Loading stories…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-16 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.04)' }} data-testid="testimonials-empty">
            <Heart className="w-12 h-12 mx-auto mb-4" style={{ color: '#E57A44' }} />
            <h3 className="text-2xl mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Be the first to share your story</h3>
            <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Your journey could be the encouragement someone else needs today.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16" data-testid="testimonials-grid">
            {items.map((t) => (
              <div key={t.id} className="p-8 rounded-3xl relative" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.05)' }} data-testid={`testimonial-${t.id}`}>
                <Quote className="w-6 h-6 absolute top-6 right-6 opacity-30" style={{ color: '#E57A44' }} />
                <p className="mb-6" style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                  {t.story}
                </p>
                <div className="flex items-center gap-3 pt-4" style={{ borderTop: '1px solid #E6E2D8' }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E57A4420' }}>
                    <Heart className="w-5 h-5" style={{ color: '#E57A44' }} />
                  </div>
                  <div>
                    <p style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', fontWeight: 600 }}>
                      {t.name}{t.partner_name ? ` & ${t.partner_name}` : ''}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="p-8 sm:p-10 rounded-3xl" style={{ backgroundColor: '#F3EFE7' }} data-testid="testimonials-submit-panel">
          <h2 className="text-2xl sm:text-3xl mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>Share Your Restoration</h2>
          <p className="mb-6" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>
            Your story will be reviewed and shared to encourage others. It may take a day or two to appear.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 500 }}>Your Name</label>
                <input
                  type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} required
                  placeholder="First name or first + last"
                  className="w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2"
                  style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FFFFFF', borderColor: '#E6E2D8', color: '#2C2724' }}
                  data-testid="testimonial-name"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 500 }}>Partner Name <span style={{ color: '#8C867D', fontWeight: 400 }}>(optional)</span></label>
                <input
                  type="text" value={partnerName} onChange={(e) => setPartnerName(e.target.value)} maxLength={100}
                  placeholder="If shared as a couple"
                  className="w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2"
                  style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FFFFFF', borderColor: '#E6E2D8', color: '#2C2724' }}
                  data-testid="testimonial-partner"
                />
              </div>
            </div>
            <div>
              <label className="block mb-2 text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 500 }}>Your Story</label>
              <textarea
                value={story} onChange={(e) => setStory(e.target.value)} maxLength={2000} required rows={6}
                placeholder="Share how faith, guidance, or this community helped restore your relationship…"
                className="w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2"
                style={{ fontFamily: 'Lora, serif', backgroundColor: '#FFFFFF', borderColor: '#E6E2D8', color: '#2C2724' }}
                data-testid="testimonial-story"
              />
              <p className="text-xs mt-1 text-right" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{story.length} / 2000</p>
            </div>
            <button
              type="submit" disabled={submitting}
              className="flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:hover:translate-y-0"
              style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
              data-testid="testimonial-submit"
            >
              <Send className="w-4 h-4" />
              <span>{submitting ? 'Sending…' : 'Share My Story'}</span>
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
